/**
 * Hook for a single Live Trading Room's real-time chat.
 *
 * Subscribes to the `live_room:<sessionId>` topic on mount (the same
 * tier-gated topic the WebSocket route checks via LiveRoomService.check_access),
 * listens for `chat_message`/`error` events emitted by the websocketService,
 * and exposes sendMessage() to post a new chat message.
 */

import { useEffect, useRef, useState } from 'react';
import { websocketService } from '../services/websocket';
import { useAuthStore } from '../store/authStore';
import type { LiveChatMessage } from '../types';

interface UseLiveRoomChatReturn {
  messages: LiveChatMessage[];
  isConnected: boolean;
  sendMessage: (text: string) => void;
}

export function useLiveRoomChat(sessionId: number): UseLiveRoomChatReturn {
  const [messages, setMessages] = useState<LiveChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(websocketService.isConnected());

  const isMountedRef = useRef(true);
  const topic = `live_room:${sessionId}`;

  useEffect(() => {
    isMountedRef.current = true;

    const handleChatMessage = (data: LiveChatMessage & { session_id: number }) => {
      if (!isMountedRef.current) return;
      if (data.session_id !== sessionId) return;
      setMessages((prev) => [...prev, data]);
    };

    const handleConnection = (data: { status: string }) => {
      if (!isMountedRef.current) return;
      setIsConnected(data.status === 'connected');
    };

    const token = useAuthStore.getState().token;
    const acquired = !!token;
    if (acquired) {
      websocketService.acquire(token!, [topic]);
    } else if (websocketService.isConnected()) {
      websocketService.subscribe([topic]);
    }

    websocketService.on('chat_message', handleChatMessage);
    websocketService.on('connection', handleConnection);

    setIsConnected(websocketService.isConnected());

    return () => {
      isMountedRef.current = false;
      websocketService.unsubscribe([topic]);
      websocketService.off('chat_message', handleChatMessage);
      websocketService.off('connection', handleConnection);
      if (acquired) {
        websocketService.release();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const sendMessage = (text: string) => {
    websocketService.send({
      type: 'chat_message',
      session_id: sessionId,
      message: text,
    });
  };

  return { messages, isConnected, sendMessage };
}

export default useLiveRoomChat;
